<!--
  Campaign management — Karim, 18 Sep, corrected same day:
  "it['s] like we create facebook campaign category and inside we can do many
  campaigns and each campaign is with details."

  So: a top-level campaign IS a category/container (e.g. "Facebook"). You open
  it and add as many campaigns inside it as you want, each with its own full
  details (dates/budget/targets/funnel). A category with no children yet is
  just an empty container waiting for its first campaign. A top-level campaign
  can also stand alone with no children at all — not everything has to be a
  category first.
-->
<template>
  <div class="cm-page">
    <!-- ============ TOP-LEVEL: categories/standalone campaigns ============ -->
    <template v-if="!activeCategory">
      <section class="cm-hero">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12">
          <div>
            <h2 class="mb-0">
              Campaigns
            </h2>
            <p class="mb-0 text-muted">
              A category (e.g. "Facebook") holds many campaigns inside it, each with its own details.
            </p>
          </div>
          <button
            type="button"
            class="cm-btn cm-btn--primary"
            @click="openNew(null)"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            /> New category
          </button>
        </div>
      </section>

      <div
        v-if="loading"
        class="cm-loading"
      >
        <div
          class="spinner-border text-primary"
          role="status"
        />
      </div>

      <div
        v-else-if="!campaigns.length"
        class="cm-empty"
      >
        <feather-icon
          icon="TrendingUpIcon"
          size="30"
        />
        <h5 class="mt-2 mb-1">
          No campaigns yet
        </h5>
        <p class="text-muted mb-2">
          Start with a category like "Facebook" or "Events" — you'll add the actual campaigns (each with its own dates/budget) inside it.
        </p>
        <button
          type="button"
          class="cm-btn cm-btn--primary"
          @click="openNew(null)"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
          /> New category
        </button>
      </div>

      <div
        v-else
        class="cm-grid"
      >
        <article
          v-for="c in campaigns"
          :key="c.id"
          class="cm-card"
        >
          <div class="cm-card__top">
            <div>
              <span
                v-if="c.channel"
                class="cm-card__channel"
              >{{ c.channel }}</span>
              <h4 class="cm-card__name">
                {{ c.name }}
              </h4>
            </div>
            <span
              class="cm-status"
              :class="`cm-status--${c.status}`"
            >{{ c.status }}</span>
          </div>

          <p
            v-if="c.description"
            class="cm-card__desc"
          >
            {{ c.description }}
          </p>

          <div class="cm-card__meta">
            <span v-if="c.childCount">{{ c.childCount }} campaign{{ c.childCount === 1 ? '' : 's' }} inside</span>
            <span v-else>No campaigns inside yet</span>
            <span v-if="c.ownerName">Owner: {{ c.ownerName }}</span>
          </div>

          <campaign-funnel
            v-if="c.stats"
            :stats="c.stats"
            :money="money"
          />

          <div class="cm-card__actions">
            <button
              type="button"
              class="cm-btn cm-btn--mini cm-btn--primary"
              @click="openCategory(c)"
            >
              <feather-icon
                icon="FolderIcon"
                size="13"
              /> Open
            </button>
            <button
              type="button"
              class="cm-btn cm-btn--mini"
              @click="openEdit(c)"
            >
              <feather-icon
                icon="EditIcon"
                size="13"
              /> Edit
            </button>
            <button
              type="button"
              class="cm-btn cm-btn--mini cm-btn--danger"
              @click="remove(c)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              /> {{ c.childCount || (c.stats && (c.stats.leadsCount || c.stats.opportunitiesCount)) ? 'Close' : 'Delete' }}
            </button>
          </div>
        </article>
      </div>
    </template>

    <!-- ============ DRILL-DOWN: campaigns inside one category ============ -->
    <template v-else>
      <section class="cm-hero">
        <button
          type="button"
          class="cm-back"
          @click="closeCategory"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="15"
          /> All categories
        </button>
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-12 mt-2">
          <div>
            <span
              v-if="activeCategory.channel"
              class="cm-card__channel"
            >{{ activeCategory.channel }}</span>
            <h2 class="mb-0">
              {{ activeCategory.name }}
            </h2>
            <p class="mb-0 text-muted">
              Every campaign run under this category, each with its own dates/budget/funnel.
            </p>
          </div>
          <div class="d-flex gap-8">
            <!-- Karim, 18 Sep: "how the fuck I will update one campaign inside
                 category and it is only showing category" — there was no way
                 to edit the category's OWN details (name/budget/dates) once
                 you'd drilled into it; the only Edit button was back on the
                 top-level list. This is that missing button. -->
            <button
              type="button"
              class="cm-btn"
              @click="openEdit(activeCategory)"
            >
              <feather-icon
                icon="EditIcon"
                size="16"
              /> Edit {{ activeCategory.name }}
            </button>
            <button
              type="button"
              class="cm-btn cm-btn--primary"
              @click="openNew(activeCategory.id)"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
              /> New campaign in {{ activeCategory.name }}
            </button>
          </div>
        </div>

        <campaign-funnel
          v-if="rollup"
          :stats="rollup"
          :money="money"
          class="mt-2"
          label="Category total"
        />
      </section>

      <div
        v-if="loadingCategory"
        class="cm-loading"
      >
        <div
          class="spinner-border text-primary"
          role="status"
        />
      </div>

      <div
        v-else-if="!children.length"
        class="cm-empty"
      >
        <feather-icon
          icon="TrendingUpIcon"
          size="30"
        />
        <h5 class="mt-2 mb-1">
          Nothing inside {{ activeCategory.name }} yet
        </h5>
        <p class="text-muted mb-2">
          Add the first actual campaign — its own dates, budget, and target.
        </p>
        <button
          type="button"
          class="cm-btn cm-btn--primary"
          @click="openNew(activeCategory.id)"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
          /> New campaign in {{ activeCategory.name }}
        </button>
      </div>

      <div
        v-else
        class="cm-grid"
      >
        <article
          v-for="c in children"
          :key="c.id"
          class="cm-card"
        >
          <div class="cm-card__top">
            <h4 class="cm-card__name">
              {{ c.name }}
            </h4>
            <span
              class="cm-status"
              :class="`cm-status--${c.status}`"
            >{{ c.status }}</span>
          </div>

          <p
            v-if="c.description"
            class="cm-card__desc"
          >
            {{ c.description }}
          </p>

          <div class="cm-card__meta">
            <span v-if="c.startDate">{{ c.startDate }} → {{ c.endDate || 'ongoing' }}</span>
            <span v-if="c.budget !== null">Budget: {{ money(c.budget) }}</span>
            <span v-if="c.actualCost !== null">Spent: {{ money(c.actualCost) }}</span>
          </div>

          <campaign-funnel
            v-if="c.stats"
            :stats="c.stats"
            :money="money"
          />

          <div class="cm-card__actions">
            <button
              type="button"
              class="cm-btn cm-btn--mini"
              @click="openEdit(c)"
            >
              <feather-icon
                icon="EditIcon"
                size="13"
              /> Edit
            </button>
            <button
              type="button"
              class="cm-btn cm-btn--mini cm-btn--danger"
              @click="remove(c, true)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              /> {{ c.stats && (c.stats.leadsCount || c.stats.opportunitiesCount) ? 'Close' : 'Delete' }}
            </button>
          </div>
        </article>
      </div>
    </template>

    <!-- Create / Edit modal -->
    <div
      v-if="editing"
      class="cm-modal"
    >
      <div
        class="cm-modal__backdrop"
        @click="editing = null"
      />
      <div class="cm-modal__dialog">
        <header class="cm-modal__head">
          <h5 class="mb-0">
            {{ modalTitle }}
          </h5>
          <button
            type="button"
            class="cm-modal__close"
            @click="editing = null"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </header>
        <div class="cm-modal__body">
          <p
            v-if="isCategoryForm"
            class="cm-hint"
          >
            A category is just a name, like "Facebook" or "LinkedIn" — no dates or budget here. Open it afterwards to add the actual campaigns inside it, each with its own dates and budget.
          </p>
          <div class="form-group">
            <label>Name *</label>
            <input
              v-model="editing.name"
              type="text"
              class="form-control"
              :placeholder="isCategoryForm ? 'e.g. Facebook' : 'e.g. Facebook — Sep 2026'"
            >
          </div>

          <!-- Category fields: name + channel only, nothing dated or costed. -->
          <template v-if="isCategoryForm">
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Channel</label>
                <input
                  v-model="editing.channel"
                  list="cm-channel-suggestions"
                  type="text"
                  class="form-control"
                  placeholder="e.g. Facebook, Email, Event"
                >
                <datalist id="cm-channel-suggestions">
                  <option
                    v-for="ch in channelSuggestions"
                    :key="ch"
                    :value="ch"
                  />
                </datalist>
              </div>
              <div class="form-group col-md-6">
                <label>Status</label>
                <select
                  v-model="editing.status"
                  class="form-control"
                >
                  <option value="planned">
                    Planned
                  </option>
                  <option value="active">
                    Active
                  </option>
                  <option value="paused">
                    Paused
                  </option>
                  <option value="closed">
                    Closed
                  </option>
                </select>
              </div>
            </div>
          </template>

          <!-- Campaign fields: the actual thing that runs — dates, budget, cost, targets. -->
          <template v-else>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Status</label>
                <select
                  v-model="editing.status"
                  class="form-control"
                >
                  <option value="planned">
                    Planned
                  </option>
                  <option value="active">
                    Active
                  </option>
                  <option value="paused">
                    Paused
                  </option>
                  <option value="closed">
                    Closed
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Start date</label>
                <input
                  v-model="editing.startDate"
                  type="date"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-6">
                <label>End date</label>
                <input
                  v-model="editing.endDate"
                  type="date"
                  class="form-control"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Budget</label>
                <input
                  v-model.number="editing.budget"
                  type="number"
                  min="0"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-6">
                <label>Actual cost (for ROI)</label>
                <input
                  v-model.number="editing.actualCost"
                  type="number"
                  min="0"
                  class="form-control"
                >
              </div>
            </div>
            <div class="form-row">
              <div class="form-group col-md-6">
                <label>Target leads</label>
                <input
                  v-model.number="editing.targetLeads"
                  type="number"
                  min="0"
                  class="form-control"
                >
              </div>
              <div class="form-group col-md-6">
                <label>Target revenue</label>
                <input
                  v-model.number="editing.targetRevenue"
                  type="number"
                  min="0"
                  class="form-control"
                >
              </div>
            </div>
          </template>

          <div class="form-group">
            <label>Description</label>
            <textarea
              v-model="editing.description"
              class="form-control"
              rows="3"
            />
          </div>
        </div>
        <footer class="cm-modal__foot">
          <button
            type="button"
            class="cm-btn"
            :disabled="saving"
            @click="editing = null"
          >
            Cancel
          </button>
          <button
            type="button"
            class="cm-btn cm-btn--primary"
            :disabled="saving || !editing.name"
            @click="save"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import CampaignFunnel from './CampaignFunnel.vue'

export default {
  name: 'CampaignsCRM',
  components: { CampaignFunnel },
  data() {
    return {
      loading: false,
      loadingCategory: false,
      saving: false,
      campaigns: [],
      activeCategory: null,
      children: [],
      rollup: null,
      editing: null,
      channelSuggestions: ['Facebook', 'Google Ads', 'LinkedIn', 'Email', 'Referral', 'Event/Conference', 'Cold outreach', 'Other'],
    }
  },
  computed: {
    // Top level is ALWAYS a category (e.g. "Facebook") — no dates/budget/cost/targets.
    // Only a campaign created INSIDE a category (parentId set) gets those fields.
    isCategoryForm() {
      return !!this.editing && !this.editing.parentId
    },
    modalTitle() {
      if (!this.editing) return ''
      if (this.editing.id) return this.isCategoryForm ? 'Edit category' : 'Edit campaign'
      return this.isCategoryForm ? 'New category' : `New campaign in ${this.activeCategory ? this.activeCategory.name : ''}`
    },
  },
  mounted() {
    this.fetchCampaigns()
  },
  methods: {
    money(v) {
      const n = Number(v) || 0
      return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    async fetchCampaigns() {
      this.loading = true
      try {
        const res = await axios.get('crm/campaigns', { params: { topLevelOnly: 1 } })
        const body = decryptData(res.data.payload)
        this.campaigns = (body && body.data && body.data.campaigns) || []
      } catch (e) {
        this.notify('Failed to load campaigns', 'danger')
      } finally {
        this.loading = false
      }
    },
    /** Karim, 18 Sep: open a category and see every campaign inside it. */
    async openCategory(c) {
      this.activeCategory = c
      this.children = []
      this.rollup = null
      this.loadingCategory = true
      try {
        const res = await axios.get(`crm/campaigns/${c.id}`)
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || {}
        // Refresh the category's OWN fields too (not just children/rollup) —
        // otherwise editing the category while already inside it left the
        // header showing the pre-edit name/budget until you left and re-opened it.
        this.activeCategory = data
        this.children = data.children || []
        this.rollup = data.rollup || null
      } catch (e) {
        this.notify('Failed to load this category', 'danger')
      } finally {
        this.loadingCategory = false
      }
    },
    closeCategory() {
      this.activeCategory = null
      this.children = []
      this.rollup = null
      this.fetchCampaigns()
    },
    openNew(parentId) {
      this.editing = {
        id: null,
        parentId,
        name: '',
        channel: '',
        description: '',
        status: 'planned',
        startDate: '',
        endDate: '',
        budget: null,
        actualCost: null,
        targetLeads: null,
        targetRevenue: null,
      }
    },
    openEdit(c) {
      this.editing = { ...c }
    },
    async save() {
      if (!this.editing.name) return
      this.saving = true
      const payload = {
        parent_id: this.editing.parentId || null,
        name: this.editing.name,
        channel: this.editing.channel || null,
        description: this.editing.description || null,
        status: this.editing.status || 'planned',
        start_date: this.editing.startDate || null,
        end_date: this.editing.endDate || null,
        budget: this.editing.budget,
        actual_cost: this.editing.actualCost,
        target_leads: this.editing.targetLeads,
        target_revenue: this.editing.targetRevenue,
      }
      try {
        if (this.editing.id) {
          await axios.put(`crm/campaigns/${this.editing.id}`, payload)
        } else {
          await axios.post('crm/campaigns', payload)
        }
        this.editing = null
        this.notify('Campaign saved', 'success')
        if (this.activeCategory) {
          await this.openCategory(this.activeCategory)
        } else {
          await this.fetchCampaigns()
        }
      } catch (e) {
        this.notify('Could not save this campaign', 'danger')
      } finally {
        this.saving = false
      }
    },
    async remove(c, insideCategory) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Remove "${c.name}"? If it already has leads/opportunities (or campaigns inside it) it will be closed instead of deleted.`)) return
      try {
        await axios.delete(`crm/campaigns/${c.id}`)
        this.notify('Campaign updated', 'success')
        if (insideCategory) {
          await this.openCategory(this.activeCategory)
        } else {
          await this.fetchCampaigns()
        }
      } catch (e) {
        this.notify('Could not remove this campaign', 'danger')
      }
    },
    notify(text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title: text, icon: variant === 'danger' ? 'AlertCircleIcon' : 'CheckIcon', variant: variant || 'success' },
      })
    },
  },
}
</script>

<style scoped>
.cm-page { padding: 0; }
.cm-hero { margin-bottom: 1.2rem; }
.cm-back { border: 0; background: transparent; color: #2563eb; font-size: .82rem; cursor: pointer; display: inline-flex; align-items: center; gap: .3rem; padding: 0; }
.cm-hint { font-size: .78rem; color: #6e6b7b; background: #f8f8fc; border-radius: .4rem; padding: .5rem .7rem; }
.cm-loading { display: flex; justify-content: center; padding: 3rem 0; }
.cm-empty { text-align: center; padding: 3rem 1rem; color: #6e6b7b; }
.cm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 1rem; }
.cm-card { border: 1px solid #eee; border-radius: .6rem; padding: 1rem; background: #fff; }
.cm-card__top { display: flex; align-items: flex-start; justify-content: space-between; gap: .5rem; }
.cm-card__channel { font-size: .68rem; text-transform: uppercase; letter-spacing: .3px; color: #2563eb; font-weight: 700; }
.cm-card__name { margin: .1rem 0 0; font-size: 1.05rem; }
.cm-card__desc { font-size: .82rem; color: #6e6b7b; margin: .5rem 0; }
.cm-card__meta { display: flex; flex-wrap: wrap; gap: .6rem; font-size: .76rem; color: #6e6b7b; margin-bottom: .6rem; }
.cm-status { font-size: .68rem; padding: .15rem .5rem; border-radius: .3rem; text-transform: capitalize; background: #f4f4f4; white-space: nowrap; }
.cm-status--active { background: #e3f9e9; color: #28c76f; }
.cm-status--planned { background: #eef1fd; color: #2563eb; }
.cm-status--paused { background: #d0e1fc; color: #144faf; }
.cm-status--closed { background: #f4f4f4; color: #999; }
.cm-card__actions { display: flex; gap: .4rem; border-top: 1px solid #f0f0f5; padding-top: .6rem; }
.cm-btn { border: 1px solid #e4e4ec; background: #fff; border-radius: .35rem; padding: .4rem .8rem; font-size: .82rem; cursor: pointer; display: inline-flex; align-items: center; gap: .3rem; }
.cm-btn--mini { padding: .25rem .55rem; font-size: .74rem; }
.cm-btn--primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.cm-btn--danger { color: #ea5455; border-color: rgba(234,84,85,.35); }
.cm-modal { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center; }
.cm-modal__backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.45); }
.cm-modal__dialog { position: relative; background: #fff; border-radius: .6rem; width: min(600px, 92vw); max-height: 88vh; overflow-y: auto; box-shadow: 0 10px 40px rgba(0,0,0,.2); }
.cm-modal__head { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid #eee; }
.cm-modal__close { border: 0; background: transparent; cursor: pointer; }
.cm-modal__body { padding: 1rem 1.25rem; }
.cm-modal__foot { display: flex; justify-content: flex-end; gap: .5rem; padding: 1rem 1.25rem; border-top: 1px solid #eee; }
</style>
